import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetPlanningTreeResponse } from '@/api/generated/schemas';
import { WindowContent } from '@/shared/ui';
import {
  formatMonthParam,
  matchesTransportFilter,
  parseMonthParam,
  type CalendarTransportFilter,
} from './calendarModel';
import { buildCalendarSerieOptions } from './calendarTreeModel';
import { useCalendarMonthsData } from './useCalendarMonthsData';
import { useCalendarScroll } from './useCalendarScroll';
import {
  buildCalendarLayout,
  createCalendarLayoutCache,
  type CalendarAxis,
} from './model/calendarLayout';
import {
  extendAfter,
  extendBefore,
  windowAround,
  windowFor,
  windowForDay,
  windowKey,
  windowMonths,
  windowRange,
  type CalendarWindow,
} from './model/calendarWindow';
import { dayNumber, dayNumberOf, monthIndex, monthNames, monthOf, shiftMonth, type MonthAnchor } from './model/days'; // prettier-ignore
import { buildCalendarTerms } from './model/terms';
import { CalendarLegend } from './components/CalendarLegend';
import { CalendarMiniAxis } from './components/CalendarMiniAxis';
import { CalendarSurface, WEEKDAY_HEADER_HEIGHT } from './components/CalendarSurface';
import { CalendarToolbar } from './components/CalendarToolbar';
import { CreateTermsPopover } from './components/CreateTermsPopover';
import { TermDrawer } from './components/TermDrawer';
import './calendar.css';

/** Šířka jednoho dne na souvislé ose (px). */
const AXIS_DAY_WIDTH = 248;
/** Jak dlouho po doskrolování pulzuje rámeček termínu. */
const HIGHLIGHT_MS = 1300;
/** Přibližná výška sticky popisku měsíce — odsazení při skoku na termín. */
const MONTH_HEADER_HEIGHT = 30;
/** Jak dlouho se čeká na pruh termínu, než se skok vzdá (termín skrytý filtrem, prázdný měsíc). */
const SCROLL_TIMEOUT_MS = 2500;
/** Doraz pod `scrollend` — po této době se pulz spustí, i kdyby událost nepřišla. */
const SCROLL_SETTLE_MS = 700;
/** Odklad zápisu měsíce do URL při běžném posuvu, aby se nezahltila historie. */
const URL_DEBOUNCE_MS = 400;
/**
 * Kolik dnů kontextu nechat vlevo od dneška, když na něj osa skáče. Na ose je vidět jen pár dnů,
 * takže dnešek nalepený na levý kraj by vypadal jako začátek rozsahu.
 */
const AXIS_TODAY_LEAD_DAYS = 2;

/** Příkaz „odscrolluj na termín“ ze stromu; rostoucí `nonce` odliší opakovaný klik na týž termín. */
export interface CalendarScrollToTerm {
  termId: string;
  nonce: number;
  /**
   * Datum začátku termínu ze stromu. Okno je podle čeho posunout i tehdy, když termín ještě není
   * načtený — bez toho by skok na termín mimo okno neměl kam mířit.
   */
  date?: Date;
}

/**
 * Cíl odloženého posuvu — měsíc (obě osy), nebo konkrétní den (jen osa, tažení zmenšené osy).
 *
 * Skoky po měsících se dělají **bez animace**, a to schválně: plynulý posuv si cíl spočítá v px
 * dopředu, jenže během letu můžou nad výřezem přibýt donačtené měsíce, obsah se posune a animace
 * dojede jinam, než mířila. Návrh ostatně přesazuje výřez taky skokem.
 */
type PendingScroll = { month: MonthAnchor } | { day: number };

interface CalendarWorkspaceProps {
  /** Strom agendy — zdroj názvů produktů/sérií a sérií pro zakládací popover. */
  tree: GetPlanningTreeResponse | undefined;
  transport: CalendarTransportFilter;
  checkedTermIds: ReadonlySet<string>;
  scrollToTerm: CalendarScrollToTerm | null;
  /** Zobrazený měsíc ('YYYY-MM') z agenda state rootu — výchozí pozice po otevření i po reloadu. */
  month: string | undefined;
  onVisibleMonthChange: (month: string) => void;
  axis: CalendarAxis;
  onAxisChange: (axis: CalendarAxis) => void;
  /** „Reálná doprava“ = boxy sedí na dnech, kdy spoj skutečně jede. */
  realTime: boolean;
  onRealTimeChange: (realTime: boolean) => void;
  /** Termín otevřený v draweru (agenda state rootu). */
  termId: string | undefined;
  onOpenTerm: (termId: string) => void;
  onCloseTerm: () => void;
  /**
   * Kalendář je překrytý otevřeným detailem: zůstává připojený, aby si držel scroll i okno, ale
   * nedotahuje data, nezapisuje měsíc do URL a nerenderuje drawer (ten portáluje na `body`, takže
   * by ho skrytí outletu neschovalo a plaval by nad detailem).
   */
  covered: boolean;
}

/**
 * Kalendářový workspace plánovací agendy.
 *
 * Načtené **okno měsíců roste za pochodu** — doscrollování k hraně přidá další měsíce na tu stranu,
 * kam uživatel míří, a to v obou osách zobrazení. Osy jsou dvě: „Týdny“ (souvislé týdny pod sebou,
 * svislý posuv) a „Osa“ (jedna nepřetržitá řada dnů, vodorovný posuv se zmenšenou osou nahoře).
 * Časy dopravy se čtou buď „Logicky“ (spoj u termínu, ke kterému patří), nebo „Reálně“ (spoj na
 * dnech, kdy skutečně jede).
 *
 * Sám nic nepočítá: rozvrh dělá `buildCalendarLayout` nad čistým modelem, tady zůstává jen stav,
 * měření šířky dne, posuv a napojení na strom (skok na termín + zvýraznění).
 *
 * Je připojený po celou dobu života agendy — otevřený detail ho jen překryje (`covered`), takže po
 * jeho zavření je vidět přesně tam, kde uživatel přestal.
 */
export function CalendarWorkspace({
  tree,
  transport,
  checkedTermIds,
  scrollToTerm,
  month,
  onVisibleMonthChange,
  axis,
  onAxisChange,
  realTime,
  onRealTimeChange,
  termId,
  onOpenTerm,
  onCloseTerm,
  covered,
}: CalendarWorkspaceProps) {
  const { t, i18n } = useTranslation('venua-planner');
  const isTimeline = axis === 'timeline';

  // Výchozí pozice: deep-linkovaný měsíc, jinak ten aktuální. Okno kolem něj má rezervu na obě
  // strany, aby uživatel mohl hned scrollovat zpět i dopředu.
  const initialMonth = useRef<MonthAnchor>(
    month ? monthOf(parseMonthParam(month)) : monthOf(new Date())
  ).current;
  const [window_, setWindow] = useState<CalendarWindow>(() => windowAround(initialMonth));
  const [visibleMonth, setVisibleMonth] = useState<MonthAnchor>(initialMonth);
  const [dayWidth, setDayWidth] = useState(0);
  const [highlightedTermId, setHighlightedTermId] = useState<string | null>(null);
  // Posuvná plocha je ve STAVU, ne v refu — zmenšená osa se na ni musí umět navázat, jakmile vznikne.
  // Zvýraznění pruhu pod myší tu nehledej: řeší ho `:hover` v `calendar.css`, aby přejetí myší
  // nepřekreslovalo celý kalendář.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  /** Kalendář už doscrolloval na výchozí měsíc — teprve pak má smysl hlídat hrany. */
  const [ready, setReady] = useState(false);
  /**
   * Právě letí plynulý posuv na termín ze stromu. Donačítání je po tu dobu vypnuté — kdyby během
   * letu přibyly měsíce nad výřezem, cíl animace (spočítaný v px dopředu) by se posunul a skok by
   * přestřelil.
   */
  const [scrollBusy, setScrollBusy] = useState(false);
  const [pending, setPending] = useState<PendingScroll | null>(null);

  const visibleMonthRef = useRef(visibleMonth);
  visibleMonthRef.current = visibleMonth;
  const coveredRef = useRef(covered);
  coveredRef.current = covered;
  const monthParamRef = useRef(month);
  monthParamRef.current = month;

  const urlTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const settleCleanupRef = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      clearTimeout(urlTimerRef.current);
      clearTimeout(highlightTimerRef.current);
      settleCleanupRef.current?.();
    },
    []
  );

  const names = useMemo(() => monthNames(i18n.language), [i18n.language]);
  const weekdayLabels = useMemo(
    () => [
      t('calendar.days.mon'),
      t('calendar.days.tue'),
      t('calendar.days.wed'),
      t('calendar.days.thu'),
      t('calendar.days.fri'),
      t('calendar.days.sat'),
      t('calendar.days.sun'),
    ],
    [t]
  );

  const months = useMemo(
    () => windowMonths(window_).map((m) => new Date(m.year, m.month, 1)),
    [window_]
  );
  const monthsData = useCalendarMonthsData(months, { enabled: !covered });

  // Názvy ze stromu — pruh v kalendáři nese název produktu, tooltip navíc sérii.
  const { productNames, serieNames } = useMemo(() => {
    const products = new Map<string, string>();
    const series = new Map<string, string>();
    for (const project of tree?.projects ?? []) {
      for (const product of project.products) {
        products.set(product.productId, product.name);
        for (const serie of product.series) series.set(serie.productSerieId, serie.name);
      }
    }
    return { productNames: products, serieNames: series };
  }, [tree]);

  const formatNights = useCallback(
    (nights: number) =>
      nights === 0 ? t('calendar.bar.dayTrip') : t('calendar.bar.nightsBadge', { count: nights }),
    [t]
  );

  const terms = useMemo(() => {
    const visible = [...monthsData.eventsById.values()].filter(
      (event) =>
        checkedTermIds.has(event.termId) && matchesTransportFilter(event.transport, transport)
    );
    return buildCalendarTerms(visible, productNames, (event, label) =>
      [
        label,
        serieNames.get(event.productSerieId),
        event.name,
        t('calendar.bar.nights', { count: event.nights }),
      ]
        .filter(Boolean)
        .join(' · ')
    );
  }, [monthsData, checkedTermIds, transport, productNames, serieNames, t]);

  // Cache řádků žije mezi rendery — přírůstkový přepočet rozvrhu stojí a padá s ní.
  const layoutCache = useRef(createCalendarLayoutCache());

  const groups = useMemo(
    () =>
      buildCalendarLayout(
        {
          window: window_,
          axis,
          terms,
          dayWidth: isTimeline ? AXIS_DAY_WIDTH : dayWidth,
          today: dayNumber(new Date()),
          realTime,
          monthNames: names,
          formatNights,
        },
        layoutCache.current
      ),
    [window_, axis, terms, isTimeline, dayWidth, realTime, names, formatNights]
  );

  // --- Zobrazený měsíc → titulek a URL ---------------------------------------------------------

  const writeMonthParam = useCallback(
    (target: MonthAnchor) => {
      if (coveredRef.current) return;
      const param = formatMonthParam(new Date(target.year, target.month, 1));
      if (param !== monthParamRef.current) onVisibleMonthChange(param);
    },
    [onVisibleMonthChange]
  );

  /**
   * Poslední VYŽÁDANÝ měsíc (‹ ›, „Dnes“, skok ze stromu). Krokování se řetězí z něj, ne z toho,
   * co zrovna hlásí sledování posuvu: dokud plocha dojíždí — a hlavně během prvních vteřin, kdy
   * dopadají data měsíců a řádky pod výřezem rostou — se viditelný měsíc ještě hýbe sám a další
   * krok by z něj počítal vedle. Vynuluje se, jakmile výřez na vyžádaný měsíc opravdu dojede.
   */
  const requestedMonthRef = useRef<MonthAnchor | null>(null);

  /** Měsíc pod horní hranou výřezu (ze sledování posuvu) — do URL až s odkladem. */
  const handleVisibleMonth = useCallback(
    (next: MonthAnchor) => {
      if (requestedMonthRef.current && monthIndex(requestedMonthRef.current) === monthIndex(next)) {
        requestedMonthRef.current = null;
      }
      setVisibleMonth((prev) => (monthIndex(prev) === monthIndex(next) ? prev : next));
      clearTimeout(urlTimerRef.current);
      urlTimerRef.current = setTimeout(() => writeMonthParam(next), URL_DEBOUNCE_MS);
    },
    [writeMonthParam]
  );

  // --- Posuv na měsíc / den --------------------------------------------------------------------

  /** Posune výřez na daný měsíc. Vrací `false`, pokud v okně ještě není — pak se skok odloží. */
  const scrollToMonth = useCallback(
    (target: MonthAnchor): boolean => {
      if (!scroller) return false;
      if (isTimeline) {
        const range = windowRange(window_);
        const firstDay = dayNumberOf(target.year, target.month, 1);
        if (firstDay < range.start || firstDay > range.end) return false;
        // V týdnech je vidět celý měsíc, takže skok na jeho začátek stačí. Na ose je vidět jen pár
        // dnů — u aktuálního měsíce by tak dnešek zůstal kdesi mimo obrazovku.
        const today = dayNumber(new Date());
        const day =
          monthIndex(monthOf(new Date())) === monthIndex(target)
            ? Math.max(firstDay, today - AXIS_TODAY_LEAD_DAYS)
            : firstDay;
        scroller.scrollLeft = Math.max(0, (day - range.start) * AXIS_DAY_WIDTH);
        return true;
      }
      const block = scroller.querySelector<HTMLElement>(`[data-month="${monthIndex(target)}"]`);
      if (!block) return false;
      scroller.scrollTop = Math.max(0, block.offsetTop - WEEKDAY_HEADER_HEIGHT);
      return true;
    },
    [scroller, isTimeline, window_]
  );

  /** Skok na měsíc: okno se podle potřeby natáhne nebo přesadí, posuv počká na jeho vykreslení. */
  const goToMonth = useCallback(
    (target: MonthAnchor) => {
      requestedMonthRef.current = target;
      setWindow((current) => windowFor(current, target));
      setVisibleMonth(target);
      // Vědomý skok se do URL zapíše hned; odložený zápis z běžného posuvu by ho jinak přebil.
      clearTimeout(urlTimerRef.current);
      writeMonthParam(target);
      setPending({ month: target });
    },
    [writeMonthParam]
  );

  /** Posun osy na daný (zlomkový) první viditelný den — tažení zmenšené osy. */
  const scrollToDay = useCallback(
    (day: number) => {
      const next = windowForDay(window_, day);
      if (next !== window_) {
        setWindow(next);
        setPending({ day });
        return;
      }
      if (scroller) {
        scroller.scrollLeft = Math.max(0, (day - windowRange(window_).start) * AXIS_DAY_WIDTH);
      }
    },
    [window_, scroller]
  );

  // Odložený skok se zkouší znovu s každým přepočtem rozvrhu — cílový měsíc se do DOMu dostane
  // teprve v renderu, který má rozšířené okno.
  useEffect(() => {
    if (!pending || !scroller) return;
    if ('day' in pending) {
      const range = windowRange(window_);
      if (pending.day < range.start || pending.day > range.end) return;
      scroller.scrollLeft = Math.max(0, (pending.day - range.start) * AXIS_DAY_WIDTH);
      setPending(null);
      return;
    }
    if (scrollToMonth(pending.month)) setPending(null);
  }, [pending, groups, scroller, window_, scrollToMonth]);

  // --- Donačítání na hranách -------------------------------------------------------------------

  const handleReachStart = useCallback(() => setWindow((current) => extendBefore(current)), []);
  const handleReachEnd = useCallback(() => setWindow((current) => extendAfter(current)), []);

  /**
   * Korekce posuvu po změně začátku okna — jen pro **osu**. Vodorovné ukotvení obsahu prohlížeč
   * neumí, takže se dopočítá ručně; na ose to jde přesně, protože den má pevnou šířku a stačí
   * odečíst, o kolik dní se posunul začátek okna. Platí to na obě strany: měsíce vpředu můžou
   * přibýt (donačtení) i ubýt (oříznutí na strop okna).
   *
   * Ve svislém směru se nekoriguje nic — nativní scroll anchoring (`overflow-anchor`) to zvládne
   * sám a navíc si poradí i s tím, jak řádky narůstají, když dopadají data měsíců. Ruční korekce
   * by se s ním jen prala.
   */
  const rangeStartRef = useRef(windowRange(window_).start);
  useLayoutEffect(() => {
    const next = windowRange(window_).start;
    const previous = rangeStartRef.current;
    rangeStartRef.current = next;
    if (previous === next || !scroller || !isTimeline) return;
    scroller.scrollLeft += (previous - next) * AXIS_DAY_WIDTH;
  }, [window_, scroller, isTimeline]);

  useCalendarScroll({
    scroller,
    isTimeline,
    rangeStart: windowRange(window_).start,
    dayWidth: AXIS_DAY_WIDTH,
    windowKey: windowKey(window_),
    enabled: ready && !scrollBusy,
    onReachStart: handleReachStart,
    onReachEnd: handleReachEnd,
    onVisibleMonthChange: handleVisibleMonth,
  });

  // --- Výchozí posuv a přepnutí osy ------------------------------------------------------------

  // Přepnutí osy překreslí plochu od nuly. Ať uživatel neskončí na začátku okna, obnoví se posuv
  // na měsíc, který měl před přepnutím před sebou.
  useEffect(() => {
    setReady(false);
  }, [isTimeline]);

  useLayoutEffect(() => {
    if (ready || !scroller) return;
    // V týdnech závisí výška řádků na změřené šířce dne — dokud ji neznáme, jsou offsety měsíců
    // k ničemu a skok by mířil vedle.
    if (!isTimeline && dayWidth === 0) return;
    if (scrollToMonth(visibleMonthRef.current)) setReady(true);
  }, [ready, scroller, isTimeline, dayWidth, groups, scrollToMonth]);

  const stepMonth = useCallback(
    (delta: number) =>
      goToMonth(shiftMonth(requestedMonthRef.current ?? visibleMonthRef.current, delta)),
    [goToMonth]
  );
  const goToday = useCallback(() => goToMonth(monthOf(new Date())), [goToMonth]);

  // --- Skok na termín vybraný ve stromu --------------------------------------------------------
  // Okno rozšíříme hned, doskrolování ale musí počkat, až pruh skutečně vznikne — a ten vzniká
  // teprve poté, co doletí dotaz na jeho měsíc. Cíl proto zůstane ve stavu a efekt se o něj pokusí
  // znovu při každém přepočtu rozvrhu.

  /**
   * Pulz rámečku spustí až PO doskrolování — jinak by odezněl cestou a uživatel by u cíle nic
   * nezahlédl. `scrollend` neumí každý prohlížeč a u pruhu, který je už vidět, nemusí přijít vůbec,
   * takže je pod ním časový doraz. Čekání žije v refu, ne v úklidu efektu: efekt se přerenderuje
   * hned, jak se cíl vynuluje, a zrušil by si vlastní čekání dřív, než by doběhlo.
   */
  const highlightAfterScroll = useCallback((element: HTMLElement, id: string) => {
    settleCleanupRef.current?.();
    const run = () => {
      settleCleanupRef.current?.();
      settleCleanupRef.current = null;
      setScrollBusy(false);
      setHighlightedTermId(id);
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightedTermId(null), HIGHLIGHT_MS);
    };
    const timer = setTimeout(run, SCROLL_SETTLE_MS);
    element.addEventListener('scrollend', run);
    settleCleanupRef.current = () => {
      clearTimeout(timer);
      element.removeEventListener('scrollend', run);
    };
  }, []);

  const commandNonceRef = useRef(0);
  const [pendingTerm, setPendingTerm] = useState<CalendarScrollToTerm | null>(null);

  useEffect(() => {
    if (!scrollToTerm || scrollToTerm.nonce === commandNonceRef.current) return;
    commandNonceRef.current = scrollToTerm.nonce;
    const date = scrollToTerm.date ?? monthsData.eventsById.get(scrollToTerm.termId)?.start;
    if (date) {
      const target = monthOf(date);
      requestedMonthRef.current = target;
      setWindow((current) => windowFor(current, target));
      setVisibleMonth(target);
      clearTimeout(urlTimerRef.current);
      writeMonthParam(target);
    }
    setPendingTerm(scrollToTerm);
  }, [scrollToTerm, monthsData, writeMonthParam]);

  useEffect(() => {
    if (!pendingTerm || !scroller) return;
    const bar = scroller.querySelector<HTMLElement>(
      `[data-term-id="${CSS.escape(pendingTerm.termId)}"]`
    );
    if (!bar) return;
    setPendingTerm(null);

    setScrollBusy(true);
    const barRect = bar.getBoundingClientRect();
    const rootRect = scroller.getBoundingClientRect();
    if (isTimeline) {
      scroller.scrollTo({
        left: Math.max(0, barRect.left - rootRect.left + scroller.scrollLeft - rootRect.width / 3),
        behavior: 'smooth',
      });
    } else {
      const offset = WEEKDAY_HEADER_HEIGHT + MONTH_HEADER_HEIGHT + 24;
      scroller.scrollTo({
        top: Math.max(0, barRect.top - rootRect.top + scroller.scrollTop - offset),
        behavior: 'smooth',
      });
    }

    highlightAfterScroll(scroller, pendingTerm.termId);
  }, [pendingTerm, groups, isTimeline, scroller, highlightAfterScroll]);

  // Pojistka: kdyby pruh nikdy nevznikl (termín skrytý filtrem dopravy), cíl po chvíli zahoď.
  useEffect(() => {
    if (!pendingTerm) return;
    const timer = setTimeout(() => {
      setPendingTerm(null);
      setScrollBusy(false);
    }, SCROLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pendingTerm]);

  const serieOptions = useMemo(() => (tree ? buildCalendarSerieOptions(tree) : []), [tree]);
  const renderDayAction = useCallback(
    (isoDay: string) =>
      serieOptions.length > 0 ? <CreateTermsPopover date={isoDay} series={serieOptions} /> : null,
    [serieOptions]
  );

  const drawerEvent = termId ? monthsData.eventsById.get(termId) : undefined;

  const handleMeasure = useCallback(
    (width: number) => setDayWidth((prev) => (Math.abs(width - prev) > 0.5 ? width : prev)),
    []
  );

  // Otevření termínu chodí z agendy a jeho identita se může měnit; plocha je memoizovaná, takže
  // by ji nestabilní prop překresloval zbytečně.
  const openTermRef = useRef(onOpenTerm);
  openTermRef.current = onOpenTerm;
  const handleOpenTerm = useCallback((id: string) => openTermRef.current(id), []);

  return (
    <WindowContent scroll={false} data-testid="venua-planner-calendar">
      <div className="border-phx-border rounded-phx-md bg-phx-surface shadow-phx-xs relative flex h-full min-h-0 min-w-0 flex-col border">
        <CalendarToolbar
          axis={axis}
          onAxisChange={onAxisChange}
          realTime={realTime}
          onRealTimeChange={onRealTimeChange}
          onPrevMonth={() => stepMonth(-1)}
          onNextMonth={() => stepMonth(1)}
          onToday={goToday}
        />

        {isTimeline && (
          <CalendarMiniAxis
            window={window_}
            monthNames={names}
            dayWidth={AXIS_DAY_WIDTH}
            scroller={scroller}
            onScrollToDay={scrollToDay}
          />
        )}

        <CalendarSurface
          groups={groups}
          axis={axis}
          weekdayLabels={weekdayLabels}
          highlightedTermId={highlightedTermId}
          onOpenTerm={handleOpenTerm}
          renderDayAction={renderDayAction}
          scrollRef={setScroller}
          onMeasure={handleMeasure}
        />

        <CalendarLegend />

        {!covered && <TermDrawer event={drawerEvent} onClose={onCloseTerm} />}
      </div>
    </WindowContent>
  );
}
